// ===========================================================================
// Calls between regulars, client side: the Call button on a regular's row,
// the "Calling..." sheet the caller watches, the sheet that rings on the
// other side, and the small change of dress a 1:1 chat gets while it is a
// call ("Call with Bea", "Hang up").
//
// Every rule lives on the server (calls.js): who may ring whom, where it
// rings, what a refusal says. This file only asks and shows. The two sheets
// are separate elements on purpose -- a person can be ringing somebody and be
// rung at the same moment -- and each is matched to its call by callId.
// Copy never says more about the other person than the server did: "not free
// right now" covers busy, away, and a recent no alike. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { playRingTone } from '../tones.js';

/** How often the offer sheet rings while it is open. */
export const RING_EVERY_MS = 3000;
/** How long a settled outcome ("They did not pick up") stays on the caller's sheet. */
export const OUTCOME_MS = 3500;

const PHONE_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.8 2z"/></svg>';
const HANGUP_SVG =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M10.7 13.3a15.9 15.9 0 0 1-2.6-1.9l1.2-1.6a2 2 0 0 0 .2-2.1c-.4-.9-.7-1.8-.9-2.7A2 2 0 0 0 6.7 3.5H3.7a2 2 0 0 0-2 2.2 19.8 19.8 0 0 0 1.6 5.9"/>' +
  '<path d="M22.3 11.6a19.8 19.8 0 0 0-8.6-3.1 19.6 19.6 0 0 0-8 .3"/><path d="M2 2l20 20"/></svg>';

/** "Video call" or "Voice call". Anything else reads as a call. */
export function modeLabel(mode) {
  if (mode === 'video') return 'Video call';
  if (mode === 'audio') return 'Voice call';
  return 'Call';
}

/**
 * What a CALL_STATE means on the caller's sheet: `{ text, done }`, where done
 * means the call is over and the sheet should close after a moment; null for
 * a state the sheet should simply close on (connecting: the chat renders;
 * cancelled: the caller did it). Not free, not around, and a recent no are
 * one line on purpose -- the server told us nothing more, and neither do we.
 * @param {string} state
 */
export function callStateText(state) {
  switch (state) {
    case 'ringing':
      return { text: t('Ringing...'), done: false };
    case 'declined':
      return { text: t('They did not pick up.'), done: true };
    case 'busy':
      return { text: t('They are not free right now.'), done: true };
    case 'expired':
      return { text: t('No answer.'), done: true };
    default:
      return null; // connecting, cancelled: the sheet just goes
  }
}

/**
 * What an ERROR code on an invite means on the caller's sheet, or null when
 * it is not about a call. A block reads as "not regulars" by design.
 * @param {string} code
 */
export function callErrorText(code) {
  switch (code) {
    case 'not_regulars':
      return 'You can only call your regulars.';
    case 'busy_self':
      return 'Leave the chat or the room you are in first.';
    case 'already_calling':
      return 'You are already calling someone.';
    case 'not_available':
      return 'Calls are not available right now.';
    default:
      return null;
  }
}

/** Seconds left until `until`, never negative; a missing deadline is already up. */
export function secondsLeft(until, nowMs = Date.now()) {
  const ms = Number(until);
  if (!Number.isFinite(ms)) return 0;
  return Math.max(0, Math.ceil((ms - nowMs) / 1000));
}

/**
 * The Call button for one regular's row. A tap opens a two-choice menu in
 * its place (Video / Voice) when video is allowed for this band, else it
 * calls by voice at once. Enabled only while the row says `connected`: the
 * server answers busy either way, and a button that always says no is worse
 * than one that is not lit.
 * @param {{ personId: string, handle?: string|null, connected?: boolean }} r
 * @param {{ canVideo?: boolean, onCall: (personId: string, mode: 'video'|'audio', handle: string|null) => void }} h
 * @returns {HTMLElement}
 */
export function createCallButton(r, h) {
  const wrap = el('span', { class: 'call-wrap' });
  const who = r.handle || null;
  const place = (mode) => {
    wrap.replaceChildren();
    h.onCall?.(r.personId, mode, who);
  };
  const button = el('button', {
    type: 'button',
    class: 'btn btn-secondary btn-sm regular-call',
    disabled: r.connected ? null : '',
    title: r.connected ? `Call ${who || 'them'}` : 'They are not around right now',
    'aria-label': `Call ${who || 'this regular'}`,
    onClick: () => {
      if (!h.canVideo) return place('audio');
      // The chooser, in the button's place. Cancel puts the button back.
      const menu = el('span', { class: 'call-menu', role: 'group', 'aria-label': 'Call how?' }, [
        el('button', { type: 'button', class: 'btn btn-primary btn-xs', text: t('Video'), onClick: () => place('video') }),
        el('button', { type: 'button', class: 'btn btn-secondary btn-xs', text: t('Voice'), onClick: () => place('audio') }),
        el('button', { type: 'button', class: 'btn btn-ghost btn-xs', text: 'x', 'aria-label': 'Cancel', onClick: () => wrap.replaceChildren(button) }),
      ]);
      wrap.replaceChildren(menu);
    },
  }, [el('span', { html: PHONE_SVG }), el('span', { text: t('Call') })]);
  wrap.append(button);
  return wrap;
}

function removeSheets(selector) {
  for (const n of document.querySelectorAll(selector)) n.remove();
}

/**
 * The caller's sheet: "Calling Bea...", with Cancel. Opened the moment they
 * tap, before the server has said anything, so `callId` is learned from the
 * first CALL_STATE (ringing). `setState` takes any CALL_STATE for this sheet;
 * `fail` takes an ERROR's text. Both settle the sheet and close it soon after.
 * `onClose` hears `{ connecting }`: true when the sheet went because the call
 * is connecting (MATCHED follows), false for every way a call comes to nothing.
 * @param {{ handle: string|null, mode: string, onCancel: (callId: string|null) => void, onClose?: (info: { connecting: boolean }) => void }} h
 * @returns {{ root: HTMLElement, callId: string|null, setState: (m: { callId?: string, state: string }) => void, fail: (text: string) => void, close: (info?: { connecting?: boolean }) => void, isOpen: () => boolean }}
 */
export function showCallingSheet(h) {
  removeSheets('.call-sheet.calling');
  const who = h.handle || 'your regular';
  const line = el('p', { class: 'stage-offer-body call-line', text: t('Asking...') });
  let closed = false;
  let timer = null;
  const cancelBtn = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm',
    text: t('Cancel'),
    onClick: () => {
      const id = api.callId;
      api.close();
      h.onCancel?.(id);
    },
  });
  const root = el('div', { class: 'stage-offer call-sheet calling', role: 'dialog', 'aria-label': `Calling ${who}` }, [
    el('p', { class: 'stage-offer-title', text: `Calling ${who}...` }),
    el('p', { class: 'stage-offer-body muted call-mode', text: `${modeLabel(h.mode)}. They can decline; nothing happens until they pick up.` }),
    line,
    el('div', { class: 'stage-offer-actions' }, [cancelBtn]),
  ]);
  const settle = (text) => {
    line.textContent = text;
    root.classList.add('settled');
    cancelBtn.textContent = 'Close';
    clearTimeout(timer);
    timer = setTimeout(() => api.close(), OUTCOME_MS);
  };
  const api = {
    root,
    callId: null,
    isOpen: () => !closed,
    setState(m) {
      if (closed || !m) return;
      if (m.callId && !api.callId) api.callId = m.callId;
      const meaning = callStateText(m.state);
      if (!meaning) return api.close({ connecting: m.state === 'connecting' });
      if (meaning.done) settle(meaning.text);
      else line.textContent = meaning.text;
      return undefined;
    },
    fail(text) {
      if (closed) return;
      settle(text || 'That call could not be placed.');
    },
    close(info = {}) {
      if (closed) return;
      closed = true;
      clearTimeout(timer);
      root.remove();
      h.onClose?.({ connecting: !!info.connecting });
    },
  };
  document.body.append(root);
  return api;
}

/**
 * The sheet that rings: who is calling, how, Accept and Decline, and the
 * seconds left. Rings every RING_EVERY_MS while open; stops on any response,
 * on cancelled/expired from the server, and when the count runs out (which
 * also answers no, so a stale sheet cannot be accepted after the fact).
 * @param {{ callId: string, fromHandle: string|null, mode: string, until: number,
 *           onRespond: (callId: string, accept: boolean) => void, onClose?: () => void }} h
 * @returns {{ root: HTMLElement, callId: string, close: () => void, isOpen: () => boolean }}
 */
export function showOfferSheet(h) {
  removeSheets('.call-sheet.offer');
  const who = h.fromHandle || 'A regular';
  const count = el('span', { class: 'call-count', role: 'timer', 'aria-label': 'Seconds to answer' });
  let closed = false;
  let answered = false;
  const answer = (accept) => {
    if (answered) return;
    answered = true;
    api.close();
    h.onRespond?.(h.callId, accept);
  };
  const root = el('div', { class: 'stage-offer call-sheet offer', role: 'dialog', 'aria-label': `${who} is calling` }, [
    el('p', { class: 'stage-offer-title' }, [el('span', { text: `${who} is calling` }), count]),
    el('p', {
      class: 'stage-offer-body',
      text: `${modeLabel(h.mode)}. It starts camera-off and text, like any chat here: your camera opens only when you both say so.`,
    }),
    el('div', { class: 'stage-offer-actions' }, [
      el('button', { class: 'btn btn-primary btn-sm', text: t('Pick up'), onClick: () => answer(true) }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Decline'), onClick: () => answer(false) }),
    ]),
  ]);
  const tick = () => {
    const s = secondsLeft(h.until);
    count.textContent = `${s}s`;
    if (s <= 0) answer(false);
  };
  tick();
  const ticker = setInterval(tick, 500);
  playRingTone();
  const ringer = setInterval(playRingTone, RING_EVERY_MS);
  const api = {
    root,
    callId: h.callId,
    isOpen: () => !closed,
    close() {
      if (closed) return;
      closed = true;
      clearInterval(ticker);
      clearInterval(ringer);
      root.remove();
      h.onClose?.();
    },
  };
  document.body.append(root);
  return api;
}

/**
 * Dress a 1:1 chat as a call, or undress it. The chat screen (ui/chat.js)
 * knows nothing about calls: this reaches into the rendered header and the
 * skip control by class, so the header says "Call with Bea" and the skip
 * button says "Hang up" -- the same button, the same message to the server,
 * a different promise about what happens after (nothing: nobody is put back
 * in the queue). `handle` null takes it all back.
 * @param {HTMLElement} root  the mounted app root
 * @param {string|null} handle
 */
export function setCallChrome(root, handle) {
  if (!root) return;
  const on = !!handle;
  root.querySelector('.chat')?.classList.toggle('in-call', on);
  const title = root.querySelector('.panel-title');
  if (title && on) title.textContent = `Call with ${handle}`;
  const skip = root.querySelector('.skip-wrap > .btn');
  if (!skip) return;
  // Red, the kick button's red, not the primary color: nobody should hit it
  // looking for "next".
  if (on) {
    if (!skip.dataset.skipHtml) skip.dataset.skipHtml = skip.innerHTML;
    skip.innerHTML = HANGUP_SVG;
    skip.title = 'Hang up';
    skip.setAttribute('aria-label', 'Hang up');
    skip.dataset.label = 'Hang up';
    skip.classList.add('hangup', 'btn-danger');
    skip.classList.remove('btn-primary');
  } else if (skip.dataset.skipHtml) {
    skip.innerHTML = skip.dataset.skipHtml;
    delete skip.dataset.skipHtml;
    skip.title = 'Skip to the next person';
    skip.setAttribute('aria-label', 'Skip');
    skip.dataset.label = 'Skip';
    skip.classList.remove('hangup', 'btn-danger');
    skip.classList.add('btn-primary');
  }
}

export default { createCallButton, showCallingSheet, showOfferSheet, setCallChrome, modeLabel, callStateText, callErrorText, secondsLeft, RING_EVERY_MS, OUTCOME_MS };
